import { useEffect, useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { AbilityFactory, Action, AppAbility } from '@open-commerce/oc-authorization-policies';

import {
  Retailer_Network_Users_Sort_By,
  Retailer_Network_Users_Sort_Order,
  useCurrentUserAbilitiesQuery,
} from '@occc/graphql';
import config from '@occc/config';
import {
  useGetCurrentUserRetailerNetworkUsers,
  useGetOpenCommerceUserCurrent,
} from '@occc-app/dataAccessLayer/openCommerceUser/queries';
import { authService } from '@occc-app/Auth/authService';

import { AppContextValue } from '../AppContext';

type UseGetUserDataProps = {
  skip: boolean;
};

type UseGetUserDataReturnProps = {
  appContextValue: AppContextValue;
  error: ApolloError | undefined;
  loading: boolean;
  currentUserAbilities: AppAbility;
};

export const useGetAppContextData = ({ skip }: UseGetUserDataProps): UseGetUserDataReturnProps => {
  // Fetching Open Commerce User data
  const {
    data: openCommerceUserCurrentData,
    loading: isOpenCommerceUserCurrentLoading,
    error: openCommerceUserCurrentError,
  } = useGetOpenCommerceUserCurrent({
    skip,
  });
  const user = openCommerceUserCurrentData?.openCommerceUserCurrent || null;

  // Fetching Retailer Network Highest Role User data
  const isRetailerConnectEnabled = config.retailerConnect.enabled;
  const {
    data: currentUserRetailerNetworkUsersData,
    loading: isCurrentUserRetailerNetworkUsersLoading,
    error: currentUserRetailerNetworkUsersError,
  } = useGetCurrentUserRetailerNetworkUsers({
    pageSize: 1,
    sort: {
      direction: Retailer_Network_Users_Sort_Order.Desc,
      field: Retailer_Network_Users_Sort_By.Role,
    },
    skip: skip || !isRetailerConnectEnabled,
  });
  const retailerNetworkHighestRoleUser = currentUserRetailerNetworkUsersData?.[0] || null;

  // Fetching user abilities to define what permissions user has
  const {
    data: currentUserAbilitiesData,
    loading: isCurrentUserAbilitiesLoading,
    error: currentUserAbilitiesError,
  } = useCurrentUserAbilitiesQuery({
    skip,
  });
  const currentUserAbilities = useMemo(() => {
    const userAbilities = currentUserAbilitiesData?.currentUserAbilities || [];
    const subjectRules = AbilityFactory.createSubjectRulesFromUserAbilities(
      userAbilities.map((item) => {
        const action = item.action as Action;
        const conditions = {
          uuidIn: item?.conditions?.uuidIn
            ? item.conditions.uuidIn.map((uuid: string) => uuid)
            : undefined,
        };
        const fields = item.fields ?? undefined;
        const scope = item.scope ?? undefined;

        return {
          ...item,
          action,
          conditions,
          fields,
          scope,
        };
      }),
    );
    const abilities = AbilityFactory.instantiateAbilities(subjectRules);

    return abilities;
  }, [currentUserAbilitiesData]);

  // Check do we have all required data and if not - remove tokens
  useEffect(() => {
    const hasNoUserData = !user && !isOpenCommerceUserCurrentLoading;

    if (hasNoUserData) {
      authService.clearSession();
    }
  }, [user, isOpenCommerceUserCurrentLoading, currentUserAbilities, isCurrentUserAbilitiesLoading]);

  const appContextValue = useMemo(
    () => ({
      user,
      retailerNetworkHighestRoleUser,
    }),
    [user, retailerNetworkHighestRoleUser],
  );

  const error =
    openCommerceUserCurrentError ||
    currentUserRetailerNetworkUsersError ||
    currentUserAbilitiesError;
  const loading =
    isOpenCommerceUserCurrentLoading ||
    isCurrentUserRetailerNetworkUsersLoading ||
    isCurrentUserAbilitiesLoading;

  return { appContextValue, error, loading, currentUserAbilities };
};
